// fill_blank — a sentence with blanks the learner fills. Two modes:
//   • word bank present → tap a chip to select it, then tap a blank to drop it
//   • no word bank      → a free-text input per blank
// Submits once every blank has a value. The server normalises + compares against
// the accepted answers (stripped from the payload), so scoring is authoritative.

import { useState, type ReactElement } from 'react';

import type { content } from '@fajrarabi/zod-schemas';

import { ArabicText } from './primitives';
import type { ActivityRendererProps } from './types';

type TextSegment = { kind: 'text'; textKey: string };
type BlankSegment = { kind: 'blank'; blankId: string };
type Segment = TextSegment | BlankSegment;
type StrippedBlank = { id: string };
type StrippedFill = {
  instructionKey?: string | null;
  segments: Segment[];
  blanks: StrippedBlank[];
  wordBank?: string[];
};

export function FillBlank({ data, ctx, cb }: ActivityRendererProps<StrippedFill>): ReactElement {
  const { t } = ctx;
  const hasBank = Array.isArray(data.wordBank) && data.wordBank.length > 0;

  // blankId → the learner's value. For the word-bank mode the value is the
  // resolved chip text; for free-text it's the raw input.
  const [values, setValues] = useState<Record<string, string>>({});
  const [selectedChip, setSelectedChip] = useState<string | null>(null);

  function maybeSubmit(next: Record<string, string>): void {
    const filled = data.blanks.every((b) => (next[b.id] ?? '').trim().length > 0);
    if (filled) {
      cb.onAnswer({
        kind: 'fill',
        answers: data.blanks.map((b) => ({ blankId: b.id, value: next[b.id] ?? '' })),
      } satisfies content.AttemptInput);
    }
  }

  function setBlank(blankId: string, value: string): void {
    setValues((cur) => {
      const next = { ...cur, [blankId]: value };
      return next;
    });
  }

  function commitText(blankId: string, value: string): void {
    // Free-text: submit on blur/enter once all filled.
    const next = { ...values, [blankId]: value };
    setValues(next);
    maybeSubmit(next);
  }

  function dropChip(blankId: string): void {
    if (cb.locked || !selectedChip) return;
    const next = { ...values, [blankId]: selectedChip };
    setValues(next);
    setSelectedChip(null);
    maybeSubmit(next);
  }

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col">
      <p className="text-center text-lg font-semibold text-ink-700">
        {t(data.instructionKey ?? 'content.fill_blank.instruction')}
      </p>

      {/* The sentence with inline blanks. */}
      <p className="mt-6 flex flex-wrap items-center justify-center gap-x-1 gap-y-3 text-2xl leading-loose text-ink-900">
        {data.segments.map((seg, i) =>
          seg.kind === 'text' ? (
            <span key={`t-${i}`}>
              <ArabicText text={t(seg.textKey)} ctx={ctx} />
            </span>
          ) : hasBank ? (
            <button
              key={`b-${seg.blankId}`}
              type="button"
              disabled={cb.locked || !selectedChip}
              onClick={() => dropChip(seg.blankId)}
              className={`inline-flex min-h-[44px] min-w-[72px] items-center justify-center rounded-xl border-2 border-dashed px-3 text-xl font-bold transition-colors ${
                values[seg.blankId]
                  ? 'border-leaf bg-leaf/15 text-burgundy-700'
                  : 'border-burgundy-500/40 bg-burgundy-50 text-ink-500 enabled:hover:border-sun-300'
              }`}
            >
              {values[seg.blankId] ? <ArabicText text={values[seg.blankId]!} ctx={ctx} /> : '…'}
            </button>
          ) : (
            <input
              key={`b-${seg.blankId}`}
              type="text"
              dir={ctx.isRtl ? 'rtl' : 'ltr'}
              disabled={cb.locked}
              value={values[seg.blankId] ?? ''}
              onChange={(e) => setBlank(seg.blankId, e.target.value)}
              onBlur={(e) => commitText(seg.blankId, e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitText(seg.blankId, e.currentTarget.value);
              }}
              aria-label={t('content.fill_blank.blank_label')}
              className="inline-block min-h-[44px] w-28 rounded-xl border-2 border-burgundy-500/40 bg-white px-3 text-center text-xl font-bold text-ink-900 focus:border-burgundy-500 focus:outline-none"
            />
          ),
        )}
      </p>

      {/* Word bank chips. */}
      {hasBank ? (
        <div className="mt-8 flex flex-wrap justify-center gap-2">
          {data.wordBank!.map((wKey, i) => {
            const label = t(wKey);
            const used = Object.values(values).includes(label);
            return (
              <button
                key={`chip-${i}`}
                type="button"
                disabled={cb.locked || used}
                onClick={() => setSelectedChip((cur) => (cur === label ? null : label))}
                aria-pressed={selectedChip === label}
                className={`inline-flex min-h-[48px] items-center rounded-full border-2 px-4 text-lg font-bold transition-colors ${
                  used
                    ? 'border-cream-200 bg-cream-100 text-ink-500 line-through'
                    : selectedChip === label
                      ? 'border-burgundy-500 bg-burgundy-50 text-burgundy-700'
                      : 'border-cream-200 bg-white text-ink-900 hover:border-sun-300'
                }`}
              >
                <ArabicText text={label} ctx={ctx} />
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
